import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Sparkles, Loader2 } from 'lucide-react';
import {
  reportsApi,
  type ReportListItem,
  type Entitlement,
} from '../lib/reportsApi';
import {
  PageHeader, SectionCard, Button, Badge, EmptyState,
} from '../components/ui/light';

export default function Reports() {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportsApi.list(50)
      .then((r) => { setReports(r.data.reports); setEntitlement(r.data.entitlement); })
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="theme-light l-page">
      <div className="l-shell py-8">
        <PageHeader
          eyebrow="Reports"
          title="My reports"
          subtitle="Generate, revisit, and download creator monetization reports."
          actions={
            <>
              {entitlement && !entitlement.premium_unlocked && (
                <Link to="/upgrade">
                  <Button variant="secondary" iconLeft={<Sparkles className="w-4 h-4" />}>
                    Upgrade to Premium
                  </Button>
                </Link>
              )}
              <Link to="/reports/new">
                <Button variant="accent" iconLeft={<Plus className="w-4 h-4" />}>New report</Button>
              </Link>
            </>
          }
        />

        {entitlement && !entitlement.premium_unlocked && (
          <div className="l-card-muted p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="l-section-eyebrow mb-1">Free quota</div>
              <div style={{ color: 'var(--ch2-ink)' }}>
                {entitlement.free_used} of {entitlement.free_lifetime_cap} free reports used
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="h-2 w-48"
                style={{ background: 'var(--ch2-surface-3)', borderRadius: 999, overflow: 'hidden' }}
              >
                <div
                  className="h-full"
                  style={{
                    background: 'var(--ch2-accent)',
                    width: `${Math.round((entitlement.free_used / entitlement.free_lifetime_cap) * 100)}%`,
                  }}
                />
              </div>
              <Badge variant={entitlement.free_remaining > 0 ? 'accent' : 'warning'}>
                {entitlement.free_remaining} remaining
              </Badge>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center p-16">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--ch2-accent)' }} />
          </div>
        ) : reports.length === 0 ? (
          <EmptyState
            icon={<FileText className="w-6 h-6" />}
            title="No reports yet"
            description="Paste a social link to generate your first creator monetization report."
            action={
              <Link to="/reports/new">
                <Button variant="accent">Generate first report</Button>
              </Link>
            }
          />
        ) : (
          <SectionCard noPadding>
            <table className="l-table">
              <thead>
                <tr>
                  <th>Creator</th>
                  <th>Platform</th>
                  <th>Variant</th>
                  <th>Generated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="font-medium">@{r.handle ?? '—'}</div>
                      <div className="l-subtle text-xs">{r.profile_url ?? ''}</div>
                    </td>
                    <td><Badge variant="neutral">{r.platform ?? '—'}</Badge></td>
                    <td>
                      <Badge variant={r.variant === 'premium' ? 'accent' : 'neutral'}>
                        {r.variant}
                      </Badge>
                    </td>
                    <td className="l-muted">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <Link to={`/reports/${r.id}`}>
                        <Button size="sm" variant="secondary">Open</Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        )}
      </div>
    </div>
  );
}
